// =====================================================
// eventsApi.js   data layer for Community Events
//
// EVENTS_USE_MOCK = true renders demo data so the whole
// UI works before the Python endpoints exist. When the
// backend is live, flip it to false   no component
// changes needed.
//
// Real endpoint (Python + SQLite, business API):
//   GET /api/communities/<slug>/events
//   -> {
//        events: [{ id, title, description, event_date,
//                   start_time, end_time, venue_name,
//                   address, map_url, ticket_url,
//                   organizer_name, tags: [],
//                   image_urls: [up to 3] }],
//        importantDates: [{ id, date_label, label, yearly }],
//        links: { website, instagram, facebook,
//                 youtube, tiktok }
//      }
// =====================================================

import { useEffect, useState } from "react";
import { api } from "./session";

export const EVENTS_USE_MOCK = false; /* live backend is deployed */

/* ---------- small date helpers (all local-time safe) ---------- */

/* new Date("2026-09-12") parses as UTC midnight and can shift a
   day backwards in US timezones   always parse by parts instead. */
export const parseLocalDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  return new Date(y || 1970, (m || 1) - 1, d || 1);
};

export const toIsoLocal = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

export const fmtBadge = (iso) =>
  parseLocalDate(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

export const fmtLongDate = (iso) =>
  parseLocalDate(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

export function fmtTime(t) {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  if (!Number.isFinite(h)) return "";
  const mer = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m || 0).padStart(2, "0")} ${mer}`;
}

/* "6:00 – 10:00 PM" (single meridiem when both match) */
export function fmtTimeRange(ev) {
  const s = fmtTime(ev?.start_time);
  if (!s) return "";
  const e = fmtTime(ev?.end_time);
  if (!e) return s;
  const sameMer = s.slice(-2) === e.slice(-2);
  return sameMer ? `${s.slice(0, -3)} – ${e}` : `${s} – ${e}`;
}

/* ---------- mock data (relative dates so the strip always demos) ---------- */

const inDays = (n) => {
  const d = new Date();
  return toIsoLocal(
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
  );
};

const MOCK_EVENTS = [
  {
    id: 9001,
    title: "Fall heritage festival",
    description:
      "Live music, folk dancers, food vendors and a kids zone   an afternoon celebrating the community's roots. Bring the whole family.",
    event_date: inDays(3),
    start_time: "18:00",
    end_time: "22:00",
    venue_name: "Market Square",
    address: "514 W Commerce St, San Antonio, TX",
    map_url: "",
    ticket_url: "",
    organizer_name: "Community Cultural Committee",
    tags: ["Free", "Family friendly"],
    image_urls: [],
    icon: "🎪",
  },
  {
    id: 9002,
    title: "Live music night on the River Walk",
    description:
      "An open-air evening of traditional and modern music under the stars at the Arneson River Theatre. Students enter free with ID.",
    event_date: inDays(9),
    start_time: "19:30",
    end_time: "",
    venue_name: "Arneson River Theatre",
    address: "418 Villita St, San Antonio, TX",
    map_url: "",
    ticket_url: "https://goourhub.com",
    organizer_name: "Casa Cultural SA",
    tags: ["$15 tickets"],
    image_urls: [],
    icon: "🎶",
  },
  {
    id: 9003,
    title: "Community makers market",
    description:
      "Artisan vendors, homemade food, crafts for kids and live demos   meet the makers behind the community's small businesses.",
    event_date: inDays(33),
    start_time: "12:00",
    end_time: "20:00",
    venue_name: "Historic Market Square",
    address: "514 W Commerce St, San Antonio, TX",
    map_url: "",
    ticket_url: "",
    organizer_name: "GoOurHub Community Team",
    tags: ["Free entry"],
    image_urls: [],
    icon: "🛍️",
  },
];

const MOCK_IMPORTANT_DATES = [
  { id: 1, date_label: "Sep 16", label: "Independence day celebration", yearly: true },
  { id: 2, date_label: "Nov 1–2", label: "Heritage remembrance days", yearly: true },
  { id: 3, date_label: "Dec 12", label: "Community faith festival", yearly: true },
];

const MOCK_LINKS = {
  website: "https://goourhub.com",
  instagram: "https://instagram.com/goourhub",
  facebook: "https://facebook.com/goourhub",
  youtube: "",
  tiktok: "",
};

/* ---------- fetch ---------- */

export async function fetchCommunityEvents(slug) {
  if (EVENTS_USE_MOCK) {
    /* tiny delay so the loading state is visible in dev */
    await new Promise((r) => setTimeout(r, 250));
    return {
      events: MOCK_EVENTS,
      importantDates: MOCK_IMPORTANT_DATES,
      links: MOCK_LINKS,
    };
  }

  const response = await api.get(
    `/api/communities/${encodeURIComponent(slug)}/events`
  );
  const data = response?.data || {};
  return {
    events: Array.isArray(data.events) ? data.events : [],
    importantDates: Array.isArray(data.importantDates)
      ? data.importantDates
      : Array.isArray(data.important_dates)
      ? data.important_dates
      : [],
    links: data.links || null,
  };
}

export function useCommunityEvents(slug) {
  const [state, setState] = useState({
    events: [],
    importantDates: [],
    links: null,
    loading: Boolean(slug),
    error: "",
  });

  useEffect(() => {
    if (!slug) return undefined;

    let alive = true;
    setState((s) => ({ ...s, loading: true, error: "" }));

    fetchCommunityEvents(slug)
      .then((payload) => {
        if (!alive) return;
        setState({
          events: payload.events,
          importantDates: payload.importantDates,
          links: payload.links,
          loading: false,
          error: "",
        });
      })
      .catch(() => {
        if (!alive) return;
        setState({
          events: [],
          importantDates: [],
          links: null,
          loading: false,
          error: "Couldn't load events right now.",
        });
      });

    return () => {
      alive = false;
    };
  }, [slug]);

  return state;
}

/* ---------- themed placeholder icon ----------
   For events without a flyer: derive an icon from the event's own
   title + tags, so placeholders feel like the event, not a circus. */
const EMOJI_RULES = [
  [/film|screening|movie|cinema/, "🎬"],
  [/margarita|cocktail|wine|beer/, "🍹"],
  [/tamal|taco|food|cuisine|eat/, "🌮"],
  [/diwali|deepavali|lights/, "🪔"],
  [/muertos|dead|altar/, "🌼"],
  [/chariot|yatra|temple|kirtan/, "🛕"],
  [/music|concert|tejano|band|dj/, "🎶"],
  [/dance|folklor|ballet/, "💃"],
  [/market|bazaar|mercado|vendor/, "🛍️"],
  [/parade|independence|fiesta/, "🎉"],
];

export function eventEmoji(ev) {
  const haystack = `${ev?.title || ""} ${(ev?.tags || []).join(" ")}`.toLowerCase();
  for (const [pattern, emoji] of EMOJI_RULES) {
    if (pattern.test(haystack)) return emoji;
  }
  return "🎪";
}

/* ---------- submit an event (Post Event form) ---------- */

export async function submitCommunityEvent(payload) {
  const response = await api.post("/api/events", payload);
  return response?.data;
}

/* Compress a chosen photo down to a low-res JPEG data URL before
   upload: max 1200px on the long edge at ~72% quality lands around
   150-300 KB. The backend re-validates type and caps at 1.5 MB. */
export async function compressEventFlyer(file, maxDim = 1200, quality = 0.72) {
  const originalDataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("read-failed"));
    reader.readAsDataURL(file);
  });

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode-failed"));
    img.src = originalDataUrl;
  });

  const scale = Math.min(1, maxDim / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(image, 0, 0, width, height);

  let output = canvas.toDataURL("image/jpeg", quality);
  if (output.length > 1_900_000) {
    /* enormous source photo   squeeze harder to stay under the cap */
    output = canvas.toDataURL("image/jpeg", 0.55);
  }
  return output;
}

/* ---------- PDF flyers: first page -> JPEG on-device ----------
   Requires:  npm install pdfjs-dist@3.11.174
   pdf.js is loaded lazily (dynamic import) only when a PDF is
   actually picked, so it adds nothing to the normal bundle. */

async function pdfFirstPageToJpeg(file, maxDim = 1200) {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
  try {
    const workerEntry = await import(
      "pdfjs-dist/legacy/build/pdf.worker.entry"
    );
    pdfjs.GlobalWorkerOptions.workerSrc = workerEntry.default || workerEntry;
  } catch {
    /* bundler couldn't include the worker entry   fall back to CDN */
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  }

  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(3, maxDim / Math.max(base.width, base.height)) || 1;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const context = canvas.getContext("2d");
    context.fillStyle = "#ffffff"; /* PDFs can be transparent; JPEG can't */
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;

    let output = canvas.toDataURL("image/jpeg", 0.8);
    if (output.length > 1_900_000) {
      output = canvas.toDataURL("image/jpeg", 0.6);
    }
    return output;
  } finally {
    doc.destroy();
  }
}

export function isPdfFile(file) {
  return (
    file?.type === "application/pdf" || /\.pdf$/i.test(file?.name || "")
  );
}

/* One entry point for the picker: photos get compressed, PDFs get
   their first page rendered   either way a small JPEG comes out. */
export async function processFlyerFile(file) {
  return isPdfFile(file) ? pdfFirstPageToJpeg(file) : compressEventFlyer(file);
}

/* ---------- saved events (server-backed) ----------
   Hearts persist per signed-in account via /api/events/favorites*,
   mirroring business favorites. Legacy device-local hearts
   (pre-account era) migrate to the account once, then clear. */

const SAVED_EVENTS_KEY = "gohubSavedEvents";

export function useEventSaves(user) {
  const [ids, setIds] = useState(() => new Set());

  useEffect(() => {
    if (!user) {
      setIds(new Set());
      return undefined;
    }
    let alive = true;

    (async () => {
      let serverIds = new Set();
      try {
        const response = await api.get("/api/events/favorites");
        serverIds = new Set((response?.data?.eventIds || []).map(String));
      } catch {
        /* offline / hiccup   hearts stay empty until next load */
      }

      try {
        const raw = localStorage.getItem(SAVED_EVENTS_KEY);
        const local = (JSON.parse(raw || "[]") || []).map(String);
        for (const id of local) {
          if (serverIds.has(id)) continue;
          try {
            const r = await api.post("/api/events/favorites/toggle", {
              eventId: id,
            });
            if (r?.data?.saved) serverIds.add(id);
          } catch {
            /* event no longer exists (old mock ids)   skip */
          }
        }
        if (local.length) localStorage.removeItem(SAVED_EVENTS_KEY);
      } catch {
        /* corrupt local store   ignore */
      }

      if (alive) setIds(serverIds);
    })();

    return () => {
      alive = false;
    };
  }, [user]);

  const isEventSaved = (id) => ids.has(String(id));

  const toggleEventSave = async (id) => {
    const key = String(id);
    const willSave = !ids.has(key);
    setIds((prev) => {
      const next = new Set(prev);
      if (willSave) next.add(key);
      else next.delete(key);
      return next;
    });
    try {
      const response = await api.post("/api/events/favorites/toggle", {
        eventId: key,
      });
      return Boolean(response?.data?.saved);
    } catch (error) {
      setIds((prev) => {
        const next = new Set(prev);
        if (willSave) next.delete(key);
        else next.add(key);
        return next;
      });
      throw error;
    }
  };

  return { isEventSaved, toggleEventSave };
}

/* ---------- share ---------- */

export async function shareEvent(ev) {
  const u = new URL(window.location.href);
  u.searchParams.set("tab", "events");
  u.searchParams.set("event", String(ev.id));
  const url = u.toString();
  const text = `${ev.title}   community event on GoOurHub`;

  try {
    if (navigator.share) {
      await navigator.share({ title: ev.title, text, url });
      return;
    }
  } catch {
    return; /* user closed the share sheet */
  }

  try {
    await navigator.clipboard.writeText(`${text}   ${url}`);
    window.alert("Link copied.");
  } catch {
    window.alert(url);
  }
}

/* ---------- add to calendar (.ics, no dependencies) ---------- */

const icsEscape = (s) =>
  String(s || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

const icsStamp = (iso, time) =>
  `${String(iso).replace(/-/g, "")}T${String(time || "09:00").replace(":", "")}00`;

export function downloadIcs(ev) {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//GoOurHub//Community Events//EN",
    "BEGIN:VEVENT",
    `UID:goh-event-${ev.id}@goourhub.com`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`,
    `DTSTART:${icsStamp(ev.event_date, ev.start_time)}`,
  ];

  if (ev.end_time) lines.push(`DTEND:${icsStamp(ev.event_date, ev.end_time)}`);

  lines.push(`SUMMARY:${icsEscape(ev.title)}`);

  const location = [ev.venue_name, ev.address].filter(Boolean).join(", ");
  if (location) lines.push(`LOCATION:${icsEscape(location)}`);
  if (ev.description) lines.push(`DESCRIPTION:${icsEscape(ev.description)}`);
  if (ev.ticket_url) lines.push(`URL:${ev.ticket_url}`);

  lines.push("END:VEVENT", "END:VCALENDAR");

  const blob = new Blob([lines.join("\r\n")], {
    type: "text/calendar;charset=utf-8",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${String(ev.title || "event")
    .replace(/[^a-z0-9]+/gi, "-")
    .toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
